<template>
  <long-query-layout
    resource-name="anemonefish-i"
    :query-filter="queryFilter"
    :map-query-filter-for-api="mapQueryFilterForApi"
    :apply-query-filter="applyQueryFilter"
    :result-fetch-url.sync="resultTable.url"
    :columns.sync="resultTable.columns"
    :group-headers.sync="resultTable.groupHeaders"
    :active-tab.sync="activeTab"
    :customers.sync="customerCollection"
    :customer-download-only="true"
    :excel-properties-handler="excelPropertiesHandler.bind(this)"
    :convert-menu-default-query-filter="convertMenuDefaultQueryFilter"
    @result-area-size-change="updateTableSize"
  >
    <!-- -------------------------------------------------------- -->
    <!-- 分析条件 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:filter>
      <!-- 期間 -->
      <query-filter-element id="term" title="期間" contentWidth="medium">
        <filter-term v-bind.sync="queryFilter.term" />
      </query-filter-element>

      <!-- 商品分類 -->
      <query-filter-element
        id="item-compare"
        title="商品分類"
        contentWidth="medium"
      >
        <gl-select-company :company.sync="queryFilter.item.company" />
        <filter-item-compare v-bind.sync="queryFilter.item" />
      </query-filter-element>

      <!-- 店舗 -->
      <query-filter-element id="store" title="店舗" contentWidth="medium">
        <filter-store v-bind.sync="queryFilter.store" />
      </query-filter-element>

      <!-- 顧客グループ -->
      <query-filter-element id="customer" title="顧客グループ">
        <filter-customer v-bind.sync="queryFilter.customer" />
      </query-filter-element>

      <!-- オプション -->
      <query-filter-element id="option-anemonefish" title="オプション">
        <filter-option-anemonefish v-bind.sync="queryFilter.option" />
      </query-filter-element>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析結果 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:result>
      <gl-table ref="myTable" v-bind="resultTable" />
    </template>
  </long-query-layout>
</template>

<script>
// 分析条件, 分析結果のためのレイアウト
import LongQueryLayout from '@/components/layout/LongQueryLayout.vue'
// 分析条件サマリ
// 分析条件
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import FilterTerm from '@/components/FilterTerm.vue'
import FilterItemCompare from '@/components/FilterItemCompare2.vue'
import FilterStore from '@/components/FilterStore.vue'
import FilterCustomer from '@/components/FilterCustomer.vue'
import FilterOptionAnemonefish from '@/components/FilterOptionAnemonefish.vue'
// 分析条件モデル
import term from '@/queryfilter/term'
import itemCompare from '@/queryfilter/itemCompare2'
import store from '@/queryfilter/store'
import customer from '@/queryfilter/customer'
// 分析結果
import GlTable from '@/components/GlTable.vue'
import { searchCellCustomFunction } from '@/util/custom_formatters'

let hold = false

export default {
  components: {
    QueryFilterElement,
    FilterTerm,
    FilterItemCompare,
    FilterStore,
    FilterCustomer,
    FilterOptionAnemonefish,
    LongQueryLayout,
    GlTable,
  },
  data() {
    return {
      /**
       * 表示タブ（分析条件、分析結果）
       *
       * 表示タブをプログラム側から切り替えたい場合をこれを使います。
       */
      activeTab: undefined,
      /**
       * 分析条件
       */
      queryFilter: {
        term: { order: 1, ...term.get() },
        item: { order: 2, summaryKey: 'itemCompare', ...itemCompare.get() },
        store: { order: 3, ...store.get() },
        customer: { order: 4, ...customer.get() },
        option: {
          order: 5,
          summaryKey: 'optionAnemonefish',
          concurrent: 1,
          lift: 2,
          errors: [],
        },
      },
      /**
       * 分析結果
       */
      resultTable: {
        columns: undefined,
        groupHeaders: undefined,
        sortColumnName: 'compatible_base_concurrent_count',
        sortOrder: 'asc',
        url: undefined,
        width: 0,
        height: 0,
        customFormatter: {
          headerEdit: (cell, headerText, headerContent) => {
            if (!headerContent.border.top) {
              cell.style.borderTopStyle = 'none'
            }
            if (!headerContent.border.right) {
              cell.style.borderRightStyle = 'none'
            }
            if (!headerContent.border.bottom) {
              cell.style.borderBottomStyle = 'none'
            }
            if (!headerContent.border.left) {
              cell.style.borderLeftStyle = 'none'
            }
            cell.style.textAlign = headerContent.align
          },
          linkify: (rowData, cell, value, colName) => {
            cell.classList.add('customer-collection-cell')
            cell.setAttribute(
              'data-customer-collection',
              rowData.customer_filter[colName],
            )

            const toggle = e => {
              if (!this.resultTable.columns) {
                throw new Error('Column not found')
              }
              // hit the column
              const col = this.resultTable.columns.find(
                c => c.colName === colName,
              )

              const id = e.target.getAttribute('data-customer-collection')
              const text = `${rowData.item_name} - ${col.headerText}`
              const customer = { id, text }

              this.$emit('toggle-customer-collection', customer)
            }

            cell.addEventListener('mousedown', e => {
              hold = true
              document.body.classList.add('no-select')
              toggle(e)
            })
            cell.addEventListener('mouseup', () => {
              hold = false
              document.body.classList.remove('no-select')
            })

            cell.addEventListener('mouseenter', e => {
              // console.log('hold', hold)
              if (!hold) {
                return
              }
              toggle(e)
            })
          },
          linkifySearch: searchCellCustomFunction,
        },
      },
      /**
       * 顧客コレクション
       */
      customerCollection: [],
      /**
       * この画面が持つ分析条件を、クエリのペイロードに変換する
       */
      mapQueryFilterForApi: queryFilter => ({
        term: term.mapToApiPayload(queryFilter.term),
        item: itemCompare.mapToApiPayload(queryFilter.item),
        store: store.mapToApiPayload(queryFilter.store),
        customer: customer.mapToApiPayload(queryFilter.customer),
        option: {
          concurrent: queryFilter.option.concurrent,
          lift: queryFilter.option.lift,
        },
      }),
      /**
       * 渡された分析条件をこの画面の分析条件に反映する
       *
       * 渡される分析条件はこの画面のものとは限らないので、"可能な限り"復元する。
       */
      applyQueryFilter: queryFilter => {
        term.applyPayload(this.queryFilter.term, queryFilter.term)
        itemCompare.applyPayload(this.queryFilter.item, queryFilter.item)
        store.applyPayload(this.queryFilter.store, queryFilter.store)
        customer.applyPayload(this.queryFilter.customer, queryFilter.customer)
        this.queryFilter.option.concurrent = queryFilter.option.concurrent
        this.queryFilter.option.lift = queryFilter.option.lift
      },
      /**
       * Excel出力のパラメータ生成
       */
      excelPropertiesHandler() {
        const { name, order } = this.$refs.myTable.getCurrentSort()
        return {
          sortColumnName: name,
          sortOrder: order,
        }
      },
    }
  },
  methods: {
    /**
     * 結果表示テーブルのサイズを更新する
     */
    updateTableSize(size) {
      this.resultTable.width = size.width
      this.resultTable.height = size.height
    },
    // デフォルトQueryFilterの変換処理
    // 商品グループは一時的なものなのでメニューのデフォルト条件
    // としては保持しない。
    convertMenuDefaultQueryFilter(queryFilter) {
      queryFilter.item.base.itemGroupId = undefined
      queryFilter.item.compare.itemGroupId = undefined
      return queryFilter
    },
  },
}
</script>
